import { apiFetch, getOrCreateDeviceFingerprint } from './client';
import type {
  AuthTokens,
  LoginWithPasswordRequest,
  RequestOtpRequest,
  RequestOtpResponse,
  VerifyOtpRequest,
} from './types';

/**
 * Auth API surface. Pairs 1:1 with Backend `AuthController` endpoints.
 */

const camelToPascal = (input: Record<string, unknown>): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input)) {
    out[k.charAt(0).toUpperCase() + k.slice(1)] = v;
  }
  return out;
};

const pascalToCamel = <T>(input: Record<string, unknown>): T => {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input)) {
    const key = k.charAt(0).toLowerCase() + k.slice(1);
    out[key] =
      v && typeof v === 'object' && !Array.isArray(v)
        ? pascalToCamel(v as Record<string, unknown>)
        : v;
  }
  return out as T;
};

export const authApi = {
  async requestOtp(mobile: string): Promise<RequestOtpResponse> {
    const body: RequestOtpRequest = { mobile };
    const raw = await apiFetch<Record<string, unknown>>('/api/v1/auth/otp/request', {
      method: 'POST',
      body: camelToPascal(body as unknown as Record<string, unknown>),
    });
    return pascalToCamel<RequestOtpResponse>(raw);
  },

  async verifyOtp(req: VerifyOtpRequest): Promise<AuthTokens> {
    const body = {
      ...req,
      deviceFingerprint: req.deviceFingerprint ?? getOrCreateDeviceFingerprint(),
    };
    const raw = await apiFetch<Record<string, unknown>>('/api/v1/auth/otp/verify', {
      method: 'POST',
      body: camelToPascal(body as unknown as Record<string, unknown>),
    });
    return pascalToCamel<AuthTokens>(raw);
  },

  async loginWithPassword(req: LoginWithPasswordRequest): Promise<AuthTokens> {
    const body = {
      ...req,
      deviceFingerprint: req.deviceFingerprint ?? getOrCreateDeviceFingerprint(),
    };
    const raw = await apiFetch<Record<string, unknown>>('/api/v1/auth/login', {
      method: 'POST',
      body: camelToPascal(body as unknown as Record<string, unknown>),
    });
    return pascalToCamel<AuthTokens>(raw);
  },

  async refresh(refreshToken: string): Promise<AuthTokens> {
    const raw = await apiFetch<Record<string, unknown>>('/api/v1/auth/refresh', {
      method: 'POST',
      body: camelToPascal({
        refreshToken,
        deviceFingerprint: getOrCreateDeviceFingerprint(),
      }),
    });
    return pascalToCamel<AuthTokens>(raw);
  },

  async logout(refreshToken: string): Promise<void> {
    await apiFetch('/api/v1/auth/logout', {
      method: 'POST',
      body: camelToPascal({ refreshToken }),
    });
  },
};
